import { SOURCE_GRADE_TEXT, sourceGrade } from '@bk/contracts';
import type { AccountFactDto, AccountItemDto, AccountOnlineFactDto, AccountQuestionDto, AccountsDto, DocumentMismatchDto, FinancialYearDto, OnboardingState } from '@bk/contracts';
import { ownerError } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, apiUpload, errorMessage } from '../../../lib/api';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';

const day = (d: string) => d.split('-').reverse().join('/');
const money = (n: number) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: Math.abs(n) >= 1000 ? 0 : 2 }).format(n);
const num = (n: number) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(n);
const SOURCE: Record<AccountItemDto['fonte'], string> = { xbrl: 'Bilancio XBRL', piano_dei_conti: 'Piano dei conti', bilancio: 'Schemi del bilancio', nota_integrativa: 'Nota integrativa' };
/** I numeri dell'azienda senza bilancio (ADR 0155) e le loro fonti, come le legge il titolare. */
const ONLINE_LABEL: Record<AccountOnlineFactDto['campo'], string> = { fatturato: 'Fatturato', totale_bilancio: 'Totale di bilancio', anno_bilancio: 'Esercizio', addetti: 'Addetti' };
const ONLINE_SOURCE: Record<string, string> = { visura: 'visura camerale', sito: 'sito dell’azienda', registro: 'pagina pubblica con la partita IVA', online: 'pagina online', persona: 'detto da te', documento: 'documento', supposto: 'stima' };
/** Quanti conti per parte si vedono subito: gli altri con un tocco. */
const FIRST = 10;

/**
 * I conti dal bilancio (ADR 0132), sotto l'atto nel passo Visura: prima la lettura del consulente (come lavori oggi, cosa
 * è cambiato, le domande, ciò che non torna tra i documenti, cosa manca), poi i numeri chiusi: rapporti, nota e conti.
 * Solo per il titolare e i responsabili.
 */
export function Conti() {
  const { t, tenant, isManager } = useTenant();
  const qc = useQueryClient();
  // Dopo un caricamento i conti arrivano quando la lettura del documento finisce: si ricontrolla finché non ci sono.
  const [waiting, setWaiting] = useState(false);
  const conti = useTenantQuery<AccountsDto>(['onboarding', 'conti'], '/onboarding/conti', {
    refetchInterval: (d) => (d?.stato === 'in_lettura' || d?.controllo.stato === 'in_corso' || (waiting && (d?.stato !== 'pronto' || d?.controllo.stato !== 'pronto')) ? 4000 : false),
  });
  const [year, setYear] = useState<string | null>(null);
  const d = conti.data;
  if (!d) return null;
  const shown = d.esercizi.find((y) => y.chiuso_il === year) ?? d.esercizi[0] ?? null;
  const set = (x: AccountsDto) => qc.setQueryData(['tenant', tenant.id, 'onboarding', 'conti'], x);

  const answer = (id: string, risposta: string) => api<AccountsDto>(t('/onboarding/conti/risposta'), { method: 'POST', body: { id, risposta } }).then((x) => void set(x));
  const l = d.lettura ?? { fatti: [], domande: [], mancano: [] };
  const today = l.fatti.filter((f) => f.tipo === 'oggi');
  const changed = l.fatti.filter((f) => f.tipo === 'variazione');
  const reading = d.controllo.stato === 'in_corso';

  return (
    <div className="mt-4 space-y-3">
      <h2 className="text-sm font-semibold">I conti, dal bilancio</h2>

      {/* Da dove vengono i numeri (ADR 0155): il bilancio scaricato tramite API, o perché non c'è e cosa si usa. */}
      {d.origine && <p className="text-xs text-ink/60">{d.origine}</p>}
      {(d.online ?? []).length > 0 && <OnlineFacts list={d.online!} />}

      {d.stato === 'in_lettura' && <Working text="Leggo il bilancio voce per voce…" />}
      {reading && !l.fatti.length && <Working text="Leggo i conti con la nota integrativa e gli altri documenti…" />}

      {today.length > 0 && <Facts title="Come lavori oggi, secondo i conti" list={today} />}
      {changed.length > 0 && <Facts title="Cosa è cambiato rispetto all’anno prima" list={changed} />}

      {l.domande.length > 0 && (
        <section className="space-y-2">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink/50">Cosa devo chiederti</h3>
          {l.domande.map((q) => (
            <Question key={q.id} q={q} canAnswer={isManager} answer={answer} />
          ))}
        </section>
      )}

      {(d.controllo.incoerenze.length > 0 || (reading && l.fatti.length > 0)) && <Mismatches list={d.controllo.incoerenze} running={reading} canAnswer={isManager} answer={answer} />}

      {l.mancano.length > 0 && (
        <section className="rounded-2xl bg-white/60 px-4 py-3 ring-1 ring-black/5">
          <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink/50">Per dirti di più mi servirebbe</h3>
          <ul className="list-disc space-y-0.5 pl-5 text-sm text-ink/75">
            {l.mancano.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </section>
      )}

      {(d.non_letti ?? []).map((x) => (
        <p key={x.documento} className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-900">
          Non sono riuscito a leggere «{x.documento}»: {x.errore}
        </p>
      ))}

      {/* Si possono sempre aggiungere documenti da confrontare: dopo il bilancio, verbali, DVR e il resto. */}
      {isManager && (
        <Upload
          text={d.da_caricare ?? 'Non serve caricare nulla: visura e bilancio arrivano dal Registro Imprese. Se vuoi, puoi aggiungere altri documenti da confrontare.'}
          onDone={() => {
            setWaiting(true);
            void qc.invalidateQueries({ queryKey: ['tenant', tenant.id, 'onboarding'] });
          }}
        />
      )}
      {d.stato === 'assente' && !d.da_caricare && !d.origine && <p className="text-xs text-ink/50">Nessun bilancio in archivio.</p>}

      {/* I numeri restano a disposizione, chiusi: la lettura sopra dice cosa significano. */}
      {shown && (
        <details className="group rounded-2xl bg-white/60 ring-1 ring-black/5" open={!l.fatti.length && !reading}>
          <summary className="flex cursor-pointer list-none flex-wrap items-center justify-between gap-2 px-4 py-3 text-sm font-medium">
            <span>I numeri del bilancio</span>
            <span className="text-xs text-ink/50 group-open:hidden">rapporti, nota integrativa e tutti i conti</span>
          </summary>
          <div className="space-y-3 px-4 pb-4">
            {d.esercizi.length > 1 && (
              <div className="flex gap-1" role="tablist" aria-label="Esercizio">
                {d.esercizi.map((y) => (
                  <button
                    key={y.chiuso_il}
                    role="tab"
                    aria-selected={y === shown}
                    onClick={() => setYear(y.chiuso_il)}
                    className={`rounded-full px-3 py-1 text-xs font-medium ring-1 transition ${y === shown ? 'bg-ink text-white ring-ink' : 'bg-white text-ink/70 ring-black/10 hover:ring-black/25'}`}
                  >
                    {y.chiuso_il.slice(0, 4)}
                  </button>
                ))}
              </div>
            )}
            <Year y={shown} />
          </div>
        </details>
      )}
    </div>
  );
}

const STEP_TITLES: Record<string, string> = {
  visura: 'Visura',
  impresa: 'L’impresa',
  sedi: 'Sedi',
  attivita: 'Attività economiche',
  impianti: 'Impianti e attrezzature',
  software: 'Programmi e servizi online',
  organizzazione: 'Organizzazione e ruoli',
  procedure: 'Procedure',
};

function Working({ text }: { text: string }) {
  return (
    <p className="flex items-center gap-3 rounded-2xl bg-white px-5 py-4 text-sm font-medium ring-1 ring-black/5">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-accent border-t-transparent motion-reduce:animate-none" />
      {text}
    </p>
  );
}

/** I fatti che il consulente ricava dai conti: ognuno con il passo che cambia e le fonti, al passaggio del mouse. */
function Facts({ title, list }: { title: string; list: AccountFactDto[] }) {
  return (
    <section className="rounded-2xl bg-white px-4 py-3 ring-1 ring-black/5">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">{title}</h3>
      <ul className="space-y-2">
        {list.map((f) => (
          <li key={f.testo} className="text-sm">
            <p className="text-ink/85">{f.testo}</p>
            <p className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-ink/45">
              <span className="rounded-full bg-accent/10 px-2 py-0.5 font-medium text-ink/70">{STEP_TITLES[f.passo] ?? f.passo}</span>
              {sourcesOf(f).map((x) =>
                x.conto ? (
                  <span key={x.key} className="font-mono">
                    conto {x.conto}
                  </span>
                ) : (
                  <span key={x.key} title={[x.documento, ...x.citazioni.map((c) => `«${c}»`)].join('\n')}>
                    {shortName(x.documento!)}
                  </span>
                ),
              )}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Le fonti di un fatto, una volta ciascuna: lo stesso documento citato due volte si mostra una volta, con le frasi. */
function sourcesOf(f: AccountFactDto) {
  const out: Array<{ key: string; conto: string | null; documento: string | null; citazioni: string[] }> = [];
  for (const x of f.fonti) {
    const key = x.conto ? `c:${x.conto}` : `d:${x.documento}`;
    const seen = out.find((o) => o.key === key);
    if (seen) {
      if (x.citazione) seen.citazioni.push(x.citazione);
    } else out.push({ key, conto: x.conto, documento: x.documento, citazioni: x.citazione ? [x.citazione] : [] });
  }
  return out;
}

/** Il nome di un file come si legge: senza estensione né numerazione iniziale, al massimo 48 caratteri. */
const shortName = (n: string) => {
  const t = n.replace(/\.(pdf|xbrl|xml|txt|docx?|xlsx?)$/gi, '').replace(/\.(pdf|xbrl|xml)$/gi, '').replace(/^\d+\.\s*/, '').trim();
  return t.length > 48 ? `${t.slice(0, 46).trim()}…` : t;
};

/** Una domanda nata dai conti: un tocco sulla risposta, la più probabile per prima. */
function Question({ q, canAnswer, answer }: { q: AccountQuestionDto; canAnswer: boolean; answer: (id: string, risposta: string) => Promise<void> }) {
  const send = useMutation({ mutationFn: (r: string) => answer(q.id, r) });
  return (
    <div className={`rounded-2xl bg-white px-4 py-3 ring-1 ${q.risposta ? 'ring-black/5' : 'ring-accent/40'}`}>
      <p className="font-medium">{q.domanda}</p>
      <p className="mt-0.5 text-xs text-ink/55">{q.perche}</p>
      {q.risposta ? (
        <p className="mt-1 text-xs text-emerald-800">Hai risposto: {q.risposta}</p>
      ) : (
        canAnswer && (
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {q.opzioni.map((o, k) => (
              <button
                key={o}
                disabled={send.isPending}
                onClick={() => send.mutate(o)}
                className={`rounded-full px-3 py-1 text-xs ring-1 transition disabled:opacity-50 ${k === 0 ? 'bg-accent/10 text-ink ring-accent/40 hover:bg-accent/20' : 'bg-white text-ink/80 ring-black/10 hover:ring-black/25'}`}
              >
                {o}
              </button>
            ))}
          </div>
        )
      )}
      {send.error && <p className="mt-1 text-xs text-red-700">{errorMessage(send.error)}</p>}
    </div>
  );
}

function Year({ y }: { y: FinancialYearDto }) {
  const ratios = y.rapporti.filter((r) => r.valore != null);
  const nota = y.nota.filter((s) => s.voci.length);
  const empty = y.nota.filter((s) => !s.voci.length).map((s) => s.titolo.toLowerCase());
  // I conti di una fonte: la più dettagliata, le altre a scelta.
  const sources = (['piano_dei_conti', 'xbrl', 'bilancio'] as const).filter((f) => y.voci.some((v) => v.fonte === f));
  const [source, setSource] = useState<AccountItemDto['fonte'] | null>(null);
  const src = source && sources.includes(source as (typeof sources)[number]) ? source : (sources[0] ?? null);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs font-medium text-ink/60">Esercizio chiuso il {day(y.chiuso_il)}</span>
        {y.fonti.map((f) => (
          <span key={f.documento_id + f.fonte} className="rounded-full bg-white px-2.5 py-0.5 text-xs text-ink/60 ring-1 ring-black/10" title={f.documento}>
            {SOURCE[f.fonte]}
          </span>
        ))}
        {y.controlli.map((c) => (
          <span key={c.cosa} className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${c.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
            {c.ok ? '✓' : '!'} {c.cosa}: {c.dettaglio}
          </span>
        ))}
      </div>

      {ratios.length > 0 && (
        <div className="grid gap-px overflow-hidden rounded-2xl bg-black/5 ring-1 ring-black/5 sm:grid-cols-2">
          {ratios.map((r) => (
            <div key={r.chiave} className="bg-white px-4 py-3" title={r.termini.map((x) => `${x.nome}: ${x.importo == null ? '—' : num(x.importo)}${x.conti.length ? ` (conti ${x.conti.join(', ')})` : ''}`).join('\n')}>
              <p className="text-lg font-semibold tabular-nums">
                {r.unita === '€' ? money(r.valore!) : `${num(r.valore!)}${r.unita === '%' ? ' %' : ' giorni'}`}
              </p>
              <p className="text-xs text-ink/60">{r.titolo}</p>
              <p className="mt-0.5 text-[11px] text-ink/40">{r.formula}</p>
              {r.settore && (
                <p className="mt-1 text-[11px] text-ink/60" title={r.settore.fonte}>
                  Nel settore, aziende della tua dimensione: <span className="font-medium tabular-nums">{r.unita === '€' ? money(r.settore.valore) : num(r.settore.valore)}</span>
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {nota.map((s) => (
        <section key={s.chiave} className="rounded-2xl bg-white px-4 py-3 ring-1 ring-black/5">
          <h3 className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">{s.titolo}</h3>
          <dl className="space-y-1">
            {s.voci.map((v, k) => (
              <div key={k} className="grid gap-x-3 text-sm sm:grid-cols-[14rem_minmax(0,1fr)]" title={`Dalla nota integrativa: «${v.citazione}»`}>
                <dt className="text-ink/50">{v.campo}</dt>
                <dd className="min-w-0 text-ink/85">{v.valore}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
      {nota.length > 0 && empty.length > 0 && <p className="text-xs text-ink/40">La nota integrativa non scrive: {empty.join(', ')}.</p>}

      {src && (
        <section className="rounded-2xl bg-white px-4 py-3 ring-1 ring-black/5">
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-[11px] font-semibold uppercase tracking-wide text-emerald-800/80">Conti · {SOURCE[src]}</h3>
            {sources.length > 1 && (
              <div className="flex gap-1">
                {sources.map((f) => (
                  <button key={f} onClick={() => setSource(f)} className={`rounded-full px-2 py-0.5 text-[11px] ${f === src ? 'bg-ink/80 text-white' : 'text-ink/60 hover:bg-ink/5'}`}>
                    {SOURCE[f]}
                  </button>
                ))}
              </div>
            )}
          </div>
          <Parts voci={y.voci.filter((v) => v.fonte === src)} />
        </section>
      )}
    </div>
  );
}

/** I conti divisi per parte, i più grandi per primi; il codice accanto, perché le voci del progetto lo citano. */
function Parts({ voci }: { voci: AccountItemDto[] }) {
  const parts = [...new Set(voci.map((v) => v.parte ?? 'Voci'))];
  return (
    <div className="space-y-3">
      {parts.map((p) => (
        <Part key={p} title={p} voci={voci.filter((v) => (v.parte ?? 'Voci') === p && !v.aggregato).sort((a, b) => Math.abs(b.importo) - Math.abs(a.importo))} />
      ))}
    </div>
  );
}

function Part({ title, voci }: { title: string; voci: AccountItemDto[] }) {
  const [all, setAll] = useState(false);
  if (!voci.length) return null;
  const list = all ? voci : voci.slice(0, FIRST);
  return (
    <div>
      <p className="mb-1 text-xs font-medium text-ink/50">{title}</p>
      <ul className="divide-y divide-black/5 text-sm">
        {list.map((v) => (
          <li key={`${v.codice}|${v.descrizione}`} className="flex items-baseline justify-between gap-3 py-1">
            <span className="min-w-0">
              <span className="text-ink/85">{v.descrizione}</span>
              <span className="ml-2 font-mono text-[11px] text-ink/40">{v.codice}</span>
            </span>
            <span className="shrink-0 tabular-nums text-ink/80">{money(v.importo)}</span>
          </li>
        ))}
      </ul>
      {voci.length > FIRST && (
        <button className="mt-1 text-xs font-medium text-accent hover:underline" onClick={() => setAll(!all)}>
          {all ? 'meno' : `tutti i ${voci.length} conti`}
        </button>
      )}
    </div>
  );
}

/** Il bilancio di verifica (o la dichiarazione) caricato dal titolare: la stessa lettura dei documenti. */
function Upload({ text, onDone }: { text: string; onDone: () => void }) {
  const { t } = useTenant();
  const input = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<{ busy: boolean; error: string | null; sent: number }>({ busy: false, error: null, sent: 0 });
  const send = async (files: File[]) => {
    setState((s) => ({ ...s, busy: true, error: null }));
    try {
      for (const f of files) await apiUpload<OnboardingState>(t('/onboarding/documents'), f);
      setState((s) => ({ busy: false, error: null, sent: s.sent + files.length }));
      onDone();
    } catch (e) {
      setState((s) => ({ ...s, busy: false, error: errorMessage(e) }));
    }
  };
  return (
    <div className="rounded-2xl border border-dashed border-black/15 bg-white/60 px-4 py-3 text-sm">
      <p className="text-ink/70">{text}</p>
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <button type="button" disabled={state.busy} onClick={() => input.current?.click()} className="rounded-full bg-ink px-3 py-1 text-xs font-medium text-white disabled:opacity-50">
          {state.busy ? 'Carico…' : 'Carica il file'}
        </button>
        {state.sent > 0 && !state.busy && <span className="text-xs text-emerald-800">Ricevuto: lo leggo voce per voce.</span>}
        {state.error && <span className="text-xs text-red-700">{ownerError(state.error)}</span>}
      </div>
      <input ref={input} type="file" multiple hidden accept=".pdf,.xbrl,.xml,.xlsx,.xls,.csv,.txt" onChange={(e) => void send([...(e.target.files ?? [])])} />
    </div>
  );
}

/** Ciò che non torna tra i documenti (ADR 0132): nessuna correzione in silenzio, una domanda o una voce. */
function Mismatches({ list, running, canAnswer, answer }: { list: DocumentMismatchDto[]; running: boolean; canAnswer: boolean; answer: (id: string, risposta: string) => Promise<void> }) {
  return (
    <section className="space-y-2">
      <h2 className="flex items-center gap-2 text-sm font-semibold">
        Ciò che non torna tra i documenti
        {running && <span className="h-3 w-3 animate-spin rounded-full border-2 border-accent border-t-transparent motion-reduce:animate-none" aria-label="Confronto in corso" />}
      </h2>
      {running && !list.length && <p className="text-xs text-ink/50">Confronto visura, bilancio, verbali e gli altri documenti…</p>}
      {list.map((m) => (
        <Mismatch key={m.id} m={m} canAnswer={canAnswer} answer={answer} />
      ))}
    </section>
  );
}

function Mismatch({ m, canAnswer, answer }: { m: DocumentMismatchDto; canAnswer: boolean; answer: (id: string, risposta: string) => Promise<void> }) {
  const send = useMutation({ mutationFn: (r: string) => answer(m.id, r) });
  return (
    <div className={`rounded-2xl bg-white px-4 py-3 ring-1 ${m.esito === 'domanda' && !m.risposta ? 'ring-accent/40' : 'ring-black/5'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="font-medium">{m.tema}</p>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${m.esito === 'voce' ? 'bg-orange-50 text-orange-800' : m.risposta ? 'bg-emerald-50 text-emerald-800' : 'bg-amber-50 text-amber-800'}`}>
          {m.esito === 'voce' ? 'Nel progetto' : m.risposta ? 'Chiarito' : 'Da chiarire'}
        </span>
      </div>
      <ul className="mt-1 space-y-0.5 text-sm">
        {m.valori.map((v, k) => (
          <li key={k} title={`«${v.citazione}»`}>
            <span className="text-ink/50">{v.documento}:</span> <span className="text-ink/85">{v.valore}</span>
          </li>
        ))}
      </ul>
      {m.prevale && <p className="mt-1 text-xs text-ink/60">Vale: {m.prevale}</p>}
      {m.esito === 'voce' && m.voce && (
        <p className="mt-1 text-sm text-ink/80">
          {m.voce}
          {m.norma && <span className="text-ink/50"> · {m.norma}</span>}
        </p>
      )}
      {m.esito === 'domanda' && m.domanda && (
        <div className="mt-2">
          <p className="text-sm text-ink/80">{m.domanda}</p>
          {m.risposta ? (
            <p className="mt-1 text-xs text-emerald-800">Hai risposto: {m.risposta}</p>
          ) : (
            canAnswer && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {m.opzioni.map((o, k) => (
                  <button
                    key={o}
                    disabled={send.isPending}
                    onClick={() => send.mutate(o)}
                    className={`rounded-full px-3 py-1 text-xs ring-1 transition disabled:opacity-50 ${k === 0 ? 'bg-accent/10 text-ink ring-accent/40 hover:bg-accent/20' : 'bg-white text-ink/80 ring-black/10 hover:ring-black/25'}`}
                  >
                    {o}
                  </button>
                ))}
              </div>
            )
          )}
          {send.error && <p className="mt-1 text-xs text-red-700">{errorMessage(send.error)}</p>}
        </div>
      )}
    </div>
  );
}

/** Senza bilancio (ADR 0155): i numeri dell'azienda, ognuno con la sua fonte e la pagina da cui viene. */
function OnlineFacts({ list }: { list: AccountOnlineFactDto[] }) {
  const value = (f: AccountOnlineFactDto) => (f.campo === 'fatturato' || f.campo === 'totale_bilancio') && Number.isFinite(Number(f.valore)) ? money(Number(f.valore)) : f.valore;
  return (
    <ul className="space-y-1 rounded-2xl bg-white/60 px-4 py-3 text-sm ring-1 ring-black/5">
      {list.map((f) => (
        <li key={f.campo} title={f.citazione ?? undefined}>
          <span className="text-ink/60">{ONLINE_LABEL[f.campo]}:</span> {value(f)}{' '}
          <span className="text-xs text-ink/50">
            — fonte: {ONLINE_SOURCE[f.fonte] ?? f.fonte} (grado {sourceGrade(f.fonte)}, {SOURCE_GRADE_TEXT[sourceGrade(f.fonte)]})
            {f.rif && /^https?:\/\//.test(f.rif) ? (
              <>
                {' '}
                <a className="text-accent hover:underline" href={f.rif} target="_blank" rel="noreferrer">
                  {f.rif.replace(/^https?:\/\/(www\.)?/, '').split('/')[0]}
                </a>
              </>
            ) : f.rif ? ` (${f.rif})` : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
